import { FeatureFlagKey } from "@redotech/feature-flags-model/keys";
import { useRequiredContext } from "@redotech/react-util/context";
import { useQuery } from "@tanstack/react-query";
import { RedoPlatformAuthRpcClientContext } from "../platform/redo-platform-rpc-client-provider";
import { TeamContext } from "../team";

export interface AskRedoEnabledState {
  readonly pending: boolean;
  readonly enabled: boolean;
}

/**
 * Feature flag for the Ask Redo new chat UI.
 *
 * Reads the `ASK_REDO_UI` LaunchDarkly flag scoped to the current
 * team. While `pending`, callers should treat the flag as "not yet
 * known" rather than off — `AskRedoPage` in particular must wait for
 * the query to resolve before deciding whether to redirect, or a
 * direct page load on `/ask-redo` would bounce the user home before
 * the flag answer arrives. Defaults to `false` when LD is unreachable
 * (e.g. local dev without `LAUNCH_DARKLY_SDK_KEY`).
 *
 * When flag-on:
 *   - Homepage input navigates to `/ask-redo` (new full page)
 *   - Top nav "Ask Redo" button opens the new slide-out
 *   - Legacy `HelpChatGlobal` / `GlobalAI` stay mounted but never
 *     get triggered
 */
export function useAskRedoEnabled(): AskRedoEnabledState {
  const platformRpcClient = useRequiredContext(
    RedoPlatformAuthRpcClientContext,
  );
  const team = useRequiredContext(TeamContext);
  const teamId = String(team._id);

  const query = useQuery({
    queryKey: ["featureFlag", FeatureFlagKey.ASK_REDO_UI, teamId],
    queryFn: async () => {
      const response = await platformRpcClient.getFeatureFlag({
        flagKey: FeatureFlagKey.ASK_REDO_UI,
      });
      return response.enabled;
    },
    staleTime: Infinity,
  });

  return { pending: query.isPending, enabled: query.data ?? false };
}
